// Enhanced Storage Service with Multiple Backup Layers
import { APP_CONFIG } from '../config/constants';

// ─── Single source of truth for the primary storage key ───────────────────────
const PRIMARY_KEY = APP_CONFIG.storage.gameState; // 'mindgarden-game-v4'

export class StorageService {
  static init() {
    console.log('🔧 Initializing storage system... Primary key:', PRIMARY_KEY);
    this.migrateLegacyKeys();
    this.createEmergencyBackup();
    this.startAutoBackupRotation();
    this.logVersion();
    console.log('✅ Storage system initialized');
  }

  static getStorageKeys() {
    return {
      gameState:       PRIMARY_KEY,
      userProfile:     'mindgarden-profile-v1',
      autoBackup1:     'mindgarden-backup-1',
      autoBackup2:     'mindgarden-backup-2',
      autoBackup3:     'mindgarden-backup-3',
      emergencyBackup: 'mindgarden-emergency',
      cloudSync:       'mindgarden-cloud-sync',
      lastSync:        'mindgarden-last-sync',
      migrationLog:    'mindgarden-migrations',
      versionHistory:  'mindgarden-versions',
    };
  }

  // ── Migrate data from any old key into the current primary key ───────────────
  static migrateLegacyKeys() {
    const legacyKeys = [
      'mindgarden-game-v3',
      'mindgarden-game-v2',
      'mindgarden-game-v1',
      'idle-garden-water-v26',
      'idle-garden-water-v25',
    ];

    const existingPrimary = this.loadFromStorage(PRIMARY_KEY);
    if (existingPrimary && this.validateGameState(existingPrimary)) {
      // Already have valid data in the correct key — nothing to migrate
      return;
    }

    for (const key of legacyKeys) {
      const legacyData = this.loadFromStorage(key);
      if (legacyData && this.validateGameState(legacyData)) {
        console.log(`📦 Migrating legacy data from "${key}" → "${PRIMARY_KEY}"`);
        this.saveGameState(legacyData);
        this.logMigration(key, legacyData.seeds);
        return;
      }
    }
  }

  // ── SAVE ─────────────────────────────────────────────────────────────────────
  static saveGameState(gameState) {
    if (!this.validateGameState(gameState)) {
      console.error('❌ Invalid game state — refusing to save');
      return false;
    }

    try {
      const timestamp = Date.now();
      const stateWithMeta = {
        ...gameState,
        lastSaved: timestamp,
        version:   APP_CONFIG.version,
      };

      const keys       = this.getStorageKeys();
      const serialized = JSON.stringify(stateWithMeta);

      // Primary write
      localStorage.setItem(keys.gameState, serialized);

      // Immediate verification
      const verify = localStorage.getItem(keys.gameState);
      if (!verify) {
        console.error('❌ Primary save verification failed!');
        return false;
      }

      const parsed = JSON.parse(verify);
      if (parsed.seeds !== gameState.seeds) {
        console.error('❌ Save data mismatch! Expected seeds:', gameState.seeds, 'Got:', parsed.seeds);
        return false;
      }

      // Mirror to backup slots
      try {
        localStorage.setItem(keys.autoBackup1, serialized);
        localStorage.setItem(keys.cloudSync,   serialized);
      } catch (backupErr) {
        console.warn('⚠️ Backup write failed, primary save OK:', backupErr);
      }

      return true;
    } catch (error) {
      console.error('❌ Failed to save game state:', error);

      // Last-resort emergency save to a timestamped key
      try {
        localStorage.setItem(`mindgarden-emergency-${Date.now()}`, JSON.stringify(gameState));
        console.log('🚨 Emergency save written');
      } catch (e2) {
        console.error('❌ Emergency save also failed:', e2);
      }

      return false;
    }
  }

  // ── LOAD with fallback chain ──────────────────────────────────────────────────
  static loadGameState() {
    console.log('📂 Loading game state from key:', PRIMARY_KEY);
    const keys = this.getStorageKeys();

    // 1. Primary
    let gs = this.loadFromStorage(keys.gameState);
    if (gs && this.validateGameState(gs)) {
      console.log('✅ Loaded from primary storage. Seeds:', gs.seeds);
      return gs;
    }

    // 2. Cloud-sync mirror
    gs = this.loadFromStorage(keys.cloudSync);
    if (gs && this.validateGameState(gs)) {
      console.log('☁️ Loaded from cloud-sync mirror. Seeds:', gs.seeds);
      this.saveGameState(gs);
      return gs;
    }

    // 3. Rotating backups
    for (let i = 1; i <= 3; i++) {
      gs = this.loadFromStorage(keys[`autoBackup${i}`]);
      if (gs && this.validateGameState(gs)) {
        console.log(`🔄 Loaded from backup-${i}. Seeds:`, gs.seeds);
        this.saveGameState(gs);
        return gs;
      }
    }

    // 4. Emergency backup
    gs = this.loadFromStorage(keys.emergencyBackup);
    if (gs && this.validateGameState(gs)) {
      console.log('🚨 Loaded from emergency backup. Seeds:', gs.seeds);
      this.saveGameState(gs);
      return gs;
    }

    console.log('📭 No valid saved state found — starting fresh');
    return null;
  }

  // ── VALIDATION ───────────────────────────────────────────────────────────────
  static validateGameState(state) {
    if (!state || typeof state !== 'object') return false;
    const required = ['seeds', 'planters', 'gardeners', 'effLevel', 'autoLevel', 'compost'];
    for (const field of required) {
      if (typeof state[field] !== 'number' || isNaN(state[field]) || state[field] < 0) {
        console.warn(`❌ Validation failed on field "${field}":`, state[field]);
        return false;
      }
    }
    // Incremental games get huge numbers; removing the 1e15 cap so Quadrillions/Quintillions can be saved.
    // Setting an impossibly high cap just to prevent true infinity corruption.
    if (state.seeds > 1e100)          { console.warn('❌ Seeds value too large'); return false; }
    if (state.planters > 1e15)        { console.warn('❌ Planters value too large'); return false; }
    if (state.gardeners > 1e15)       { console.warn('❌ Gardeners value too large'); return false; }
    return true;
  }

  // ── EXPORT / IMPORT ──────────────────────────────────────────────────────────
  static exportUserData(currentState) {
    // Prefer memory state over disk state in case disk saves are failing
    const gameState = currentState || this.loadGameState();
    return JSON.stringify({
      exportedAt:         new Date().toISOString(),
      version:            APP_CONFIG.version,
      gameState,
      profile:            this.loadFromStorage(this.getStorageKeys().userProfile),
      calendar:           this.loadFromStorage('mindgarden-calendar-v1'),
      kanban:             this.loadFromStorage('mindgarden-kanban-v1'),
      habits:             this.loadFromStorage('habitGarden.v3'),
      thoughts:           this.loadFromStorage('thoughtGarden.v1'),
      backupVerification: this.createVerificationHash(gameState),
    }, null, 2);
  }

  static importUserData(jsonString) {
    try {
      const data = JSON.parse(jsonString);
      if (!data.gameState || !data.exportedAt) throw new Error('Invalid import format');

      this.createEmergencyBackup();

      if (data.gameState && this.validateGameState(data.gameState)) {
        this.saveGameState(data.gameState);
      }

      const keys = this.getStorageKeys();
      if (data.profile)   localStorage.setItem(keys.userProfile,          JSON.stringify(data.profile));
      if (data.calendar)  localStorage.setItem('mindgarden-calendar-v1',  JSON.stringify(data.calendar));
      if (data.kanban)    localStorage.setItem('mindgarden-kanban-v1',     JSON.stringify(data.kanban));
      if (data.habits)    localStorage.setItem('habitGarden.v3',           JSON.stringify(data.habits));
      if (data.thoughts)  localStorage.setItem('thoughtGarden.v1',         JSON.stringify(data.thoughts));

      return true;
    } catch (error) {
      console.error('❌ Import failed:', error);
      throw error;
    }
  }

  // ── HELPERS ──────────────────────────────────────────────────────────────────
  static loadFromStorage(key) {
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      console.warn(`⚠️ Could not load "${key}":`, e);
      return null;
    }
  }

  static createEmergencyBackup(currentState) {
    const keys    = this.getStorageKeys();
    const current = currentState || this.loadFromStorage(keys.gameState);
    if (current && this.validateGameState(current)) {
      localStorage.setItem(keys.emergencyBackup, JSON.stringify({
        ...current,
        emergencyBackupCreatedAt: Date.now(),
      }));
    }
  }

  static rotateBackups(gameState) {
    try {
      const keys = this.getStorageKeys();
      const b1   = localStorage.getItem(keys.autoBackup1);
      const b2   = localStorage.getItem(keys.autoBackup2);
      if (b2) localStorage.setItem(keys.autoBackup3, b2);
      if (b1) localStorage.setItem(keys.autoBackup2, b1);
      console.log('🔄 Backups rotated');
    } catch (e) {
      console.warn('⚠️ Backup rotation failed:', e);
    }
  }

  static startAutoBackupRotation() {
    setInterval(() => {
      const gs = this.loadGameState();
      if (gs) this.rotateBackups(gs);
    }, 5 * 60 * 1000);
  }

  static attemptLegacyMigration() {
    // Kept for external callers — migrateLegacyKeys() handles this on init now
    return null;
  }

  static logMigration(fromKey, seeds) {
    const keys       = this.getStorageKeys();
    const migrations = this.loadFromStorage(keys.migrationLog) || [];
    migrations.push({ timestamp: Date.now(), from: fromKey, to: PRIMARY_KEY, seedsTransferred: seeds, version: APP_CONFIG.version });
    if (migrations.length > 10) migrations.splice(0, migrations.length - 10);
    localStorage.setItem(keys.migrationLog, JSON.stringify(migrations));
  }

  static logVersion() {
    const keys     = this.getStorageKeys();
    const versions = this.loadFromStorage(keys.versionHistory) || [];
    const entry    = { version: APP_CONFIG.version, timestamp: Date.now(), userAgent: navigator.userAgent };
    if (!versions.find(v => v.version === entry.version)) {
      versions.push(entry);
      if (versions.length > 20) versions.splice(0, versions.length - 20);
      localStorage.setItem(keys.versionHistory, JSON.stringify(versions));
    }
  }

  static createVerificationHash(gameState) {
    if (!gameState) return '0';
    const str  = JSON.stringify({ seeds: gameState.seeds, compost: gameState.compost, planters: gameState.planters });
    let hash   = 0;
    for (let i = 0; i < str.length; i++) {
      const c = str.charCodeAt(i);
      hash    = ((hash << 5) - hash) + c;
      hash    = hash & hash;
    }
    return hash.toString(36);
  }

  static getRecoveryInfo() {
    const keys = this.getStorageKeys();
    return {
      primaryKey:   PRIMARY_KEY,
      primaryState: !!this.loadFromStorage(keys.gameState),
      cloudSync:    !!this.loadFromStorage(keys.cloudSync),
      backups: {
        backup1:   !!this.loadFromStorage(keys.autoBackup1),
        backup2:   !!this.loadFromStorage(keys.autoBackup2),
        backup3:   !!this.loadFromStorage(keys.autoBackup3),
        emergency: !!this.loadFromStorage(keys.emergencyBackup),
      },
      migrations: this.loadFromStorage(keys.migrationLog) || [],
      versions:   this.loadFromStorage(keys.versionHistory) || [],
    };
  }

  static forceRecovery() {
    console.log('🚨 Attempting force recovery...');
    const keys    = this.getStorageKeys();
    const sources = [keys.cloudSync, keys.autoBackup1, keys.autoBackup2, keys.autoBackup3, keys.emergencyBackup];
    for (const src of sources) {
      const data = this.loadFromStorage(src);
      if (data && this.validateGameState(data)) {
        console.log('✅ Recovery from:', src);
        this.saveGameState(data);
        return data;
      }
    }
    console.log('❌ Force recovery failed');
    return null;
  }
}